import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { PricingModel } from '../../../../../../models/pricing-model.model';
import { PricingParameters } from '../../../../../../models/quote-item-data.model';
import { QuoteItemDataPricingCadObject } from '../quote-item-data-editor.service';
import { ParameterEditorComponent } from '../../../../../shared/parameter-editor/parameter-editor.component';
import {
  buildSchemaBackedParameterValues,
  sanitizeParameterValuesForSave,
} from '../../../../../shared/parameter-editor/parameter-editor.utils';

interface CadParameterGroup {
  cadObject: QuoteItemDataPricingCadObject;
  cadId: string;
  parameters: Record<string, string | null>;
}

@Component({
  selector: 'app-quote-item-pricing-parameters',
  imports: [CommonModule, Dialog, Button, ParameterEditorComponent],
  templateUrl: './quote-item-pricing-parameters.component.html',
})
export class QuoteItemPricingParametersComponent implements OnChanges {
  @Input() pricingModel: PricingModel | null = null;
  @Input() pricingParameters: PricingParameters | null | undefined = null;
  @Input() cadObjects: QuoteItemDataPricingCadObject[] = [];

  @Output() saved = new EventEmitter<PricingParameters | null>();
  @Output() closed = new EventEmitter<void>();

  cadParameterGroups: CadParameterGroup[] = [];

  get parameterSchemas() {
    return this.pricingModel?.parameter_schemas ?? {};
  }

  get hasParameters(): boolean {
    return Object.keys(this.parameterSchemas).length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['pricingModel'] ||
      changes['pricingParameters'] ||
      changes['cadObjects']
    ) {
      this.initializeParameters();
    }
  }

  save(): void {
    const cleanedParams: PricingParameters = {};

    for (const group of this.cadParameterGroups) {
      cleanedParams[group.cadId] = sanitizeParameterValuesForSave(
        group.parameters,
      );
    }

    this.saved.emit(
      Object.keys(cleanedParams).length > 0 ? cleanedParams : null,
    );
  }

  onParameterEditorChange(
    cadId: string,
    event: { key: string; value: string | null },
  ): void {
    const group = this.cadParameterGroups.find(
      (candidate) => candidate.cadId === cadId,
    );

    if (!group) return;

    group.parameters[event.key] = event.value;
  }

  private initializeParameters(): void {
    const existingParams = this.pricingParameters || {};

    this.cadParameterGroups = this.cadObjects.map((cadObject) => {
      const cadId = String(cadObject.id);
      return {
        cadObject,
        cadId,
        parameters: buildSchemaBackedParameterValues(
          this.parameterSchemas,
          existingParams[cadId],
        ),
      };
    });
  }
}
